import React from "react"
import { useOutletContext } from "react-router-dom"
import { Tag, Space, Tooltip } from "antd"
import { ExclamationCircleOutlined, WarningOutlined } from "@ant-design/icons"

import { ago } from "@components/Date"
import Identity from "@components/Identity"
import { hasAccess } from "@components/Authorization"
import { ROLES_MAP } from "@components/Domain"
import { useAppContext } from "@components/AppContext"
import { UserGroupsColumn } from "@components/Column"

import TableRowActions from "../TableRowActions"

const LABEL_CONTROLLER = "This accountant controls the business's bank accounts."
const LABEL_2FA = "2FA"
const LABEL_NO = "No"
const LABEL_YES = "Yes"
const LABEL_SMS = "SMS"
const LABEL_TOTP = "App"
const LABEL_NAME = "Name"
const LABEL_ROLES = "Roles"
const LABEL_GROUPS = "Groups"
const LABEL_LAST_SIGNED_IN = "Last Signed In"

const getRoleColor = roleId => {
  const role = ROLES_MAP[roleId]

  if (!role) {
    return []
  }

  const { tagColor } = role

  return tagColor
}

const getRoleTitle = roleId => {
  const role = ROLES_MAP[roleId]

  if (!role) {
    return ""
  }

  const { title } = role

  return title
}

const useTableColumns = (openUpdateModal) => {
  const { organization } = useOutletContext()
  const { isConsoleOrganization } = useAppContext()

  const { controllerId } = organization

  const isController = user =>
    controllerId === user.id

  const nameColumnProps = {
    key: "name",
    title: LABEL_NAME,
    render: (name, user) => (
        <Identity strong id={user.id}>
          <Space>
            {name}
            {
              isController(user) && (
                <Tooltip title={LABEL_CONTROLLER} placement="top">
                  <ExclamationCircleOutlined />
                </Tooltip>
              )
            }
          </Space>
        </Identity>
      ),
    compute: item => `${item.givenName} ${item.familyName}`,
    isSearchable: true
  }

  const emailColumnProps = {
    key: "email",
    isSearchable: true
  }

  const userGroupsColumnProps = {
    key: "userGroupIds",
    title: LABEL_GROUPS,
    render: (_, { userGroupIds = [] }) =>
      <UserGroupsColumn userGroupIds={userGroupIds}/>
  }

  const rolesColumnProps = {
    key: "roles",
    title: LABEL_ROLES,
    compute: ({ roles }) => getRoleTitle(roles[0]),
    isSortingEnabled: true,
    render: (value, { roles }) =>
      <Space size={0}>
        {
          roles.map((role, key) =>
            <Tag
              key={key}
              color={getRoleColor(role)}
              bordered={false}
            >
              {getRoleTitle(role)}
            </Tag>
          )
        }
      </Space>
  }

  const lastSignedInColumnProps = {
    key: "lastAuthenticatedAt",
    title: LABEL_LAST_SIGNED_IN,
    render: value => ago(value, true)
  }

  const is2FaEnabledColumnProps = {
    key: "is2FaEnabled",
    title: LABEL_2FA,
    render: (_value, user) => {
      const hasReferenceUserGroupId = Boolean(user.referenceUserGroupId)
      const is2FaDisabled = !user.is2FaEnabled

      if (hasReferenceUserGroupId) {
        return <Tag color="green">{LABEL_YES}</Tag>
      }

      const shouldShowNo = is2FaDisabled
      const { preferredMfaMethod } = user

      if (shouldShowNo) {
        return <Tag color="red" icon={<WarningOutlined />}>{LABEL_NO}</Tag>
      }

      const isApp = preferredMfaMethod === "SOFTWARE_TOKEN_MFA"

      if (isApp) {
        return <Tag color="green">{LABEL_TOTP}</Tag>
      }

      return <Tag color="green">{LABEL_SMS}</Tag>
    }
  }

  const actionsColumnProps = {
    key: "actions",
    title: false,
    render: (_, member) =>
      member.referenceUserGroupId
        ? null
        : (
            <TableRowActions
              user={member}
              openUpdateModal={openUpdateModal}
            />
          )
  }

  const columns = [
    { ...nameColumnProps },
    { ...emailColumnProps },
  ]

  if (isConsoleOrganization) {
    columns.push({ ...userGroupsColumnProps })
  }

  if (!isConsoleOrganization) {
    columns.push({ ...rolesColumnProps })
  }

  const canUpdateMembers =
    hasAccess(['users-write']) ||
    hasAccess(['fund-users-write'])

  if (canUpdateMembers) {
    columns.push(
      { ...lastSignedInColumnProps },
      { ...is2FaEnabledColumnProps, width: 72 },
      { ...actionsColumnProps, width: 48 },
    )

  } else {
    columns.push(
      { ...lastSignedInColumnProps },
      { ...is2FaEnabledColumnProps, width: 72 },
    )

  }

  return columns
}

export default useTableColumns
